import { useEffect, useMemo, useRef, useState } from "react";

import { readCelebrationPalette, rgba } from "./three/palette.js";

/**
 * The celebration moment.
 *
 * Someone has just done something that mattered to them. This is the app
 * saying so — and then getting out of the way inside two seconds.
 *
 * The default treatment is a bloom of light (Three.js, lazily loaded, see
 * `three/celebration-scene.js`): a soft warm swell behind the card with a slow
 * drift of fine motes rising through it. Deliberately not a party popper. In a
 * product where the subject is how a person feels, fanfare is the wrong
 * register — the feeling to aim at is quiet pride, the light in the room
 * changing for a moment, not an arcade victory.
 *
 * Three paths, chosen at the moment it fires:
 *
 *   still   `prefers-reduced-motion` — a static warm glow that fades up and
 *           away. Opacity only: nothing travels, nothing scales. Driven from
 *           rAF rather than CSS because the foundation collapses every
 *           animation duration to ~0 under reduced motion, which would make a
 *           keyframed fade finish before it was seen.
 *   bloom   WebGL is available and the scene module loads. The good one.
 *   legacy  No WebGL, or the chunk failed to arrive — the original canvas
 *           confetti, kept intact below so this component always has something
 *           to show.
 *
 * The `{ active, onDone }` contract is unchanged. `onDone` still fires once,
 * when the moment is over.
 */

/** Roughly the length of an exhale. Matches CELEBRATION_MS in the scene. */
const STILL_MS = 1800;

let webglSupport = null;

/**
 * Is there a WebGL context to be had? Cached: probing costs a context, and the
 * browser only keeps a small number of them alive.
 */
function supportsWebGL() {
  if (webglSupport !== null) return webglSupport;
  if (typeof document === "undefined") return (webglSupport = false);
  try {
    const probe = document.createElement("canvas");
    const gl = probe.getContext("webgl2") || probe.getContext("webgl");
    webglSupport = Boolean(gl);
    gl?.getExtension("WEBGL_lose_context")?.loseContext();
  } catch {
    webglSupport = false;
  }
  return webglSupport;
}

function prefersReducedMotion() {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

const overlayStyle = (zIndex) => ({
  position: "fixed",
  inset: 0,
  width: "100%",
  height: "100%",
  pointerEvents: "none",
  zIndex,
});

export default function Confetti({ active, onDone, zIndex = 1000 }) {
  // idle → one of bloom | still | legacy → done. `done` renders nothing, so a
  // finished celebration leaves no canvas sitting over the screen.
  const [phase, setPhase] = useState("idle");
  const canvasRef = useRef(null);
  const glowRef = useRef(null);
  const doneRef = useRef(onDone);

  // Read once per treatment, not per render: the tokens can't change inside the
  // two seconds this is on screen.
  const palette = useMemo(
    () => (phase === "still" ? readCelebrationPalette() : null),
    [phase]
  );

  useEffect(() => {
    doneRef.current = onDone;
  }, [onDone]);

  useEffect(() => {
    if (!active) {
      setPhase("idle");
      return;
    }
    setPhase(prefersReducedMotion() ? "still" : supportsWebGL() ? "bloom" : "legacy");
  }, [active]);

  // ── The bloom ────────────────────────────────────────────────────────────
  useEffect(() => {
    if (phase !== "bloom") return;
    const canvas = canvasRef.current;
    if (!canvas) return;

    let cancelled = false;
    let scene = null;

    import("./three/celebration-scene.js")
      .then(({ createCelebration }) => {
        if (cancelled) return;
        scene = createCelebration({
          canvas,
          palette: readCelebrationPalette(),
          onComplete: () => {
            if (cancelled) return;
            setPhase("done");
            doneRef.current?.();
          },
        });
      })
      .catch(() => {
        // The chunk didn't arrive (offline cold start, cache miss). Rather than
        // swallow the moment, fall through to the confetti that ships in the
        // main bundle.
        if (!cancelled) setPhase("legacy");
      });

    return () => {
      cancelled = true;
      scene?.dispose();
    };
  }, [phase]);

  // ── The still treatment ──────────────────────────────────────────────────
  useEffect(() => {
    if (phase !== "still") return;
    const node = glowRef.current;
    if (!node) return;

    let raf = 0;
    const start = performance.now();

    const frame = () => {
      const t = (performance.now() - start) / STILL_MS;
      if (t >= 1) {
        node.style.opacity = "0";
        setPhase("done");
        doneRef.current?.();
        return;
      }
      // Up over the first third, held, then away. No movement of any kind.
      const up = Math.min(1, t / 0.3);
      const down = 1 - Math.max(0, (t - 0.62) / 0.38);
      node.style.opacity = String(Math.max(0, Math.min(up, down)));
      raf = requestAnimationFrame(frame);
    };
    raf = requestAnimationFrame(frame);

    return () => cancelAnimationFrame(raf);
  }, [phase]);

  if (!active || phase === "idle" || phase === "done") return null;

  if (phase === "still" && palette) {
    return (
      <div
        ref={glowRef}
        aria-hidden="true"
        style={{
          ...overlayStyle(zIndex),
          opacity: 0,
          background:
            `radial-gradient(circle at 50% 47%, ` +
            `${rgba(palette.warm, palette.dark ? 0.3 : 0.38)} 0%, ` +
            `${rgba(palette.warm, palette.dark ? 0.19 : 0.24)} 30%, ` +
            `${rgba(palette.cool, palette.dark ? 0.12 : 0.15)} 52%, ` +
            `${rgba(palette.cool, 0)} 74%)`,
        }}
      />
    );
  }

  if (phase === "legacy") {
    return (
      <LegacyConfetti
        zIndex={zIndex}
        onDone={() => {
          setPhase("done");
          doneRef.current?.();
        }}
      />
    );
  }

  return <canvas ref={canvasRef} aria-hidden="true" style={overlayStyle(zIndex)} />;
}

/**
 * The original canvas confetti, unchanged, as the last-resort fallback for a
 * device with no WebGL at all. Kept verbatim on purpose: it is the known-good
 * path, and this is not the code that should be carrying a redesign.
 */
function LegacyConfetti({ onDone, zIndex }) {
  const canvasRef = useRef(null);
  const particlesRef = useRef([]);
  const rafRef = useRef(null);
  const doneRef = useRef(onDone);

  useEffect(() => {
    doneRef.current = onDone;
  }, [onDone]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    canvas.width = canvas.offsetWidth;
    canvas.height = canvas.offsetHeight;
    const colors = ["#7EB8A4", "#A8D5C5", "#D88BAE", "#5DAF87", "#D4A04A", "#ffffff"];
    particlesRef.current = Array.from({ length: 120 }, () => ({
      x: canvas.width / 2 + (Math.random() - 0.5) * 200,
      y: canvas.height * 0.4,
      vx: (Math.random() - 0.5) * 14,
      vy: -Math.random() * 18 - 6,
      color: colors[Math.floor(Math.random() * colors.length)],
      size: Math.random() * 8 + 4,
      rotation: Math.random() * 360,
      rotSpeed: (Math.random() - 0.5) * 12,
      shape: Math.random() > 0.5 ? "rect" : "circle",
      alpha: 1,
    }));
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      let alive = false;
      particlesRef.current.forEach((p) => {
        p.x += p.vx;
        p.y += p.vy;
        p.vy += 0.5;
        p.vx *= 0.99;
        p.rotation += p.rotSpeed;
        p.alpha = Math.max(0, p.alpha - 0.012);
        if (p.alpha > 0) alive = true;
        ctx.save();
        ctx.globalAlpha = p.alpha;
        ctx.translate(p.x, p.y);
        ctx.rotate((p.rotation * Math.PI) / 180);
        ctx.fillStyle = p.color;
        if (p.shape === "rect")
          ctx.fillRect(-p.size / 2, -p.size / 4, p.size, p.size / 2);
        else {
          ctx.beginPath();
          ctx.arc(0, 0, p.size / 2, 0, Math.PI * 2);
          ctx.fill();
        }
        ctx.restore();
      });
      if (alive) rafRef.current = requestAnimationFrame(animate);
      else {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        doneRef.current?.();
      }
    };
    rafRef.current = requestAnimationFrame(animate);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  return <canvas ref={canvasRef} aria-hidden="true" style={overlayStyle(zIndex)} />;
}
